/**
 * CADA NUMERO DE ARQUITECTURA CON SU DUENO Y SU FECHA (SALA-ARQ-VISTA-T1 partes 1 y 2).
 *
 * DE DONDE SALE. SALA-ARQ-AUTO-T1 (nexo-api PR 429) toma la foto de arquitectura cada dia y
 * publica nexo_private.v_salud_vigente: la ultima foto de cada (ambito, clave, sujeto) con su
 * dueno, su fuente y vencida = mas de 7 dias. A la sirve en GET /api/v1/salud/vigentes
 * (COORD de P #22705, respuesta de A #22706).
 *
 * LA REGLA:
 *   - un numero vencido se pinta SIN DATO con su dueno: el numero viejo NO sale grande
 *     (lo dice el COMMENT de la vista: un dial vencido no es el estado de hoy)
 *   - un numero sin valor es "sin dato", no cero
 *   - los vencidos van primero: son lo que alguien debe
 *   - el dueno sale de la respuesta; la letra corta se deriva del codigo (ARGY-API-... -> A),
 *     nunca de una lista escrita aqui
 *   - la lectura tolera camelCase (DTO) y snake_case (vista)
 *
 * Sin DOM: lo prueba scripts/check-salud-vigentes.mjs.
 */

const pick = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};
const numero = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** La letra con que la casa nombra a cada Argy: ARGY-API-LOCAL-001 -> A, ARES-001 -> Ares. */
export function duenoCorto(codigo) {
  const c = String(codigo ?? '').trim();
  if (!c) return 'sin dueno';
  const argy = /^ARGY-([A-Z])/i.exec(c);
  if (argy) return argy[1].toUpperCase();
  if (/^ARES/i.test(c)) return 'Ares';
  return c;
}

/** Las filas del endpoint, normalizadas. Nunca lanza. */
export function leerVigentes(filas) {
  const lista = Array.isArray(filas) ? filas : [];
  return lista
    .filter((f) => f && typeof f === 'object')
    .map((f) => {
      const vencida = pick(f, 'vencida', 'Vencida');
      return {
        ambito: String(pick(f, 'ambito', 'Ambito') ?? ''),
        clave: String(pick(f, 'clave', 'Clave') ?? '').trim(),
        sujeto: pick(f, 'sujeto', 'Sujeto'),
        fecha: String(pick(f, 'fecha', 'Fecha') ?? '').slice(0, 10),
        valor: numero(pick(f, 'valor', 'Valor')),
        unidad: String(pick(f, 'unidad', 'Unidad') ?? ''),
        direccionMeta: String(pick(f, 'direccionMeta', 'direccion_meta', 'DireccionMeta') ?? ''),
        dueno: pick(f, 'dueno', 'Dueno'),
        fuente: pick(f, 'fuente', 'Fuente'),
        vencida: vencida === true || vencida === 'true' || vencida === 't'
      };
    })
    .filter((v) => v.clave !== '');
}

/** Lo que se pinta de un numero: estado 'vencida' | 'sin_dato' | 'vivo'. */
export function caraDelVigente(v) {
  const quien = duenoCorto(v.dueno);
  if (v.vencida) {
    return {
      estado: 'vencida',
      grande: 'SIN DATO',
      frase: 'Vencido: el ultimo es del ' + (v.fecha || 'dia que no se sabe') + '. Lo debe ' + quien + '.'
    };
  }
  if (v.valor === null) {
    return { estado: 'sin_dato', grande: 'sin dato', frase: 'No llego el numero. Lo debe ' + quien + '.' };
  }
  const texto = Number.isInteger(v.valor) ? String(v.valor) : String(Number(v.valor.toFixed(1)));
  return {
    estado: 'vivo',
    grande: texto + (v.unidad === 'pct' || v.unidad === '%' ? '%' : ''),
    frase: 'Medido el ' + (v.fecha || 'dia que no se sabe') + ' por ' + quien + '.'
  };
}

const ORDEN = { vencida: 0, sin_dato: 1, vivo: 2 };

/** Las tarjetas de la Sala, vencidas primero, y el resumen de quien debe cuantos. */
export function salaDeVigentes(filas) {
  const tarjetas = leerVigentes(filas)
    .map((v) => ({
      ...v,
      titulo: v.clave.replace(/_/g, ' ') + (v.sujeto && v.sujeto !== 'casa' ? ' · ' + v.sujeto : ''),
      quien: duenoCorto(v.dueno),
      cara: caraDelVigente(v)
    }))
    .sort((a, b) => (ORDEN[a.cara.estado] - ORDEN[b.cara.estado])
      || a.clave.localeCompare(b.clave, 'es') || String(a.sujeto ?? '').localeCompare(String(b.sujeto ?? ''), 'es'));
  const deben = {};
  for (const t of tarjetas) if (t.cara.estado !== 'vivo') deben[t.quien] = (deben[t.quien] || 0) + 1;
  return {
    tarjetas,
    total: tarjetas.length,
    vencidas: tarjetas.filter((t) => t.cara.estado === 'vencida').length,
    deben
  };
}
